import { Location, PlatformLocation } from '@angular/common';
import { Injectable, Type } from '@angular/core';
import { TranslatePipe } from 'pipes/translate.pipe';
import { Observable, Subscriber } from 'rxjs';
import { componentTitleProperties, componentUrlProperties, componentUrlQueries, componentUrls, urlComponents } from './url-setup';

class ComponentUrlData {
	component: any = null;
	urlProperties?: string[];
	urlQuery?: string;
	titleProperty?: { propertyName: string, translate: boolean };
	baseUrl?: string;
}

@Injectable()
export class NavigationService {

	constructor(private location: Location, private platformLocation: PlatformLocation, private translate: TranslatePipe) {
		this.navigationObservable = new Observable((observer: Subscriber<Type<Object>>) => {
			this.navigationObserver = observer;
		});
		this.platformLocation.onPopState(() => {
			this.loadUrl(this.location.path());
		});
	}

	public title = 'GORM (Group Online Resource Management)';

	private viewLoader: IViewLoader | null = null;
	private viewRemover: IViewRemover | null = null;
	private _activeUrlComponent = new ComponentUrlData();
	private _activeNavigationComponent?: ComponentUrlData;
	private navigationObservable: Observable<Type<Object>>;
	private navigationObserver?: Subscriber<Type<Object>>;

	get shownInIframe() {
		return window !== window.parent;
	}

	get activeUrlComponent() {
		return this._activeUrlComponent.component;
	}

	registerViewLoader(loader: IViewLoader, remover: IViewRemover) {
		this.viewLoader = loader;
		this.viewRemover = remover;
		this.loadUrl(this.location.path());
	}

	navigateByUrl(url: string) {
		this.location.go(url);
		this.loadUrl(url);
	}

	private loadUrl(fullPath: string) {
		fullPath = fullPath.replace(/^\//, ''); // Remove leading slash from browser URLs
		this.currentUrl = fullPath;
		const splitPath = fullPath.split('?');
		const path = splitPath[0];
		const queryString = splitPath[1];

		const pathSegments = path.split('/');
		const componentUrl = pathSegments[0] ?? '';

		const mapping = urlComponents.get(componentUrl);
		if (mapping) {
			this.loadView(mapping, { viewType: ViewType.Full, keepUrl: true }).then((component: any) => {
				const urlProperties = componentUrlProperties.get(component.__proto__);
				const urlQuery = componentUrlQueries.get(component.__proto__);
				if (urlProperties) {
					urlProperties.forEach((property, index) => {
						component[property] = pathSegments[index + 1];
					});
				}
				if (urlQuery && queryString) {
					component[urlQuery] = Object.fromEntries(new URLSearchParams(queryString));
				}
			});
		}
		else this.refreshTitle();
	}

	onNavigation(): Observable<Type<Object>> {
		return this.navigationObservable;
	}

	openView<T extends Object>(componentType: Type<T>): Promise<T> {
		return this.loadView(componentType, { viewType: ViewType.New });
	}
	navigateView<T extends Object>(parent: Object, componentType: Type<T>): Promise<T> {
		return this.loadView(componentType, { viewType: ViewType.Same, referenceContainer: parent });
	}
	openRelativeView<T extends Object>(parent: Object, identifier: string, componentType: Type<T>, avoidReplace: boolean = false): Promise<T> {
		return this.loadView(componentType,
			{
				viewType: ViewType.Relative,
				referenceContainer: parent,
				referenceId: identifier,
				avoidReplace: avoidReplace
			});
	}
	openPopup<T extends Object>(componentType: Type<T>, title?: string): Promise<T> {
		return this.loadView(componentType, { viewType: ViewType.Popup, title: title ?? 'general.default-popup-header' });
	}
	refreshView<T extends Object>(componentType: Type<T>): Promise<T> {
		return this.loadView(componentType, { viewType: ViewType.Full });
	}
	openNavigationView<T extends Object>(child: Object, identifier: string, componentType: Type<T>): Promise<T> {
		return this.loadView(componentType,
			{
				viewType: ViewType.Navigation,
				referenceContainer: child,
				referenceId: identifier
			});
	}
	openParentView<T extends Object>(child: Object, identifier: string, componentType: Type<T>): Promise<T> {
		return this.loadView(componentType,
			{
				viewType: ViewType.Parent,
				referenceContainer: child,
				referenceId: identifier,
				preserveActiveView: true
			});
	}
	closeView(component: Object): void {
		if (this.viewRemover == null) throw new Error('Error in NavigationService: A view remover was not registered.');
		this.viewRemover(component);
	}

	private loadView<T extends Object>(componentType: Type<T>, options: IViewOptions): Promise<T> {
		if (this.viewLoader == null) throw new Error('Error in NavigationService: A view loader was not registered.');
		const task = this.viewLoader(componentType, options);

		if (!options.preserveActiveView) {
			// Resolve URL action after other async callbacks have finished
			Promise.all([task]).then((results) => {
				const component = results[0];
				
				let mappedUrl = componentUrls.get(componentType);

				const urlProperties = componentUrlProperties.get((<any>component).__proto__);
				const urlQuery = componentUrlQueries.get((<any>component).__proto__);
				const titleProperty = componentTitleProperties.get((<any>component).__proto__);
				const componentUrlData = { component, urlProperties, urlQuery, baseUrl: mappedUrl, titleProperty };

				if (options.viewType === ViewType.Full) this._activeNavigationComponent = undefined;

				if (options.viewType !== ViewType.Parent && options.viewType !== ViewType.Navigation && options.viewType !== ViewType.Popup) {
					this._activeUrlComponent = componentUrlData;
					if (mappedUrl !== undefined && !options.keepUrl) this.refreshUrl();
					if (options.keepUrl) this.refreshTitle();
				}
				if (options.viewType === ViewType.Navigation) {
					this._activeNavigationComponent = componentUrlData;
				}
				if (this.navigationObserver) this.navigationObserver.next(componentType);
			});
		}

		return task;
	}

	private currentUrl?: string;
	public refreshUrl() {
		let url = this._activeUrlComponent.baseUrl;
		if (url === undefined) return;
		if (this._activeUrlComponent.urlProperties) {
			for (let property of this._activeUrlComponent.urlProperties) {
				let value = this._activeUrlComponent.component[property];
				if (!value || value == '') break;
				url += '/' + value;
			}
		}
		if (this._activeUrlComponent.urlQuery) {
			const value = this._activeUrlComponent.component[this._activeUrlComponent.urlQuery];
			if (typeof value == 'object') {
				const params = new URLSearchParams(value);
				for (let key in value) {
					if (value[key] === undefined || value[key] === null || value[key] === '') params.delete(key);
				}
				if (params.size > 0)
					url += '?' + params.toString();
			}
		}
		if (url !== this.currentUrl) {
			this.location.go(url);
			this.currentUrl = url;
		}
		this.refreshTitle();
	}
	public refreshTitle() {
		let pageTitle = this.getComponentTitle(this._activeUrlComponent) ?? `${(this._activeUrlComponent.baseUrl ? (this.translate.translate('page-title.' + this._activeUrlComponent.baseUrl) ?? this._activeUrlComponent.baseUrl) : '')}`;
		pageTitle = pageTitle != '' ? `${pageTitle} - ${this.title}` : this.title;

		if (this._activeNavigationComponent?.titleProperty) {
			document.title = `${this.getComponentTitle(this._activeNavigationComponent)} - ${pageTitle}`;
		}
		else document.title = pageTitle;
	}

	public copyUrl() {
		navigator.clipboard.writeText(window.location.href);
	}

	private getComponentTitle(componentData?: ComponentUrlData) : string | undefined {
		if (!componentData?.titleProperty) return undefined;
		const title = componentData.component[componentData.titleProperty.propertyName];
		return componentData.titleProperty.translate ? this.translate.transform(title) : title;
	}
}
export enum ViewType {
	Full, New, Relative, Parent, Popup, Same, Navigation
}
export interface IViewOptions {
	viewType: ViewType;
	referenceId?: string;
	referenceContainer?: Object;
	avoidReplace?: boolean;
	preserveActiveView?: boolean;
	keepUrl?: boolean;
	title?: string;
}
export interface IViewLoader {
	<T extends Object>(componentType: Type<T>, options: IViewOptions): Promise<T>
}
export interface IViewRemover {
	(component: Object): void
}

// Class decorator used to define the URL of a component class
export function ComponentUrl(url: string) {
	return function (componentType: Type<Object>) {
		componentUrls.set(componentType, url);
		urlComponents.set(url, componentType);
	}
}
// Property decorator indicating a property to get or set when reading or generating URLs
export function UrlParam(prototype: Object, propertyName: string) {
	// Property decorators don't access the class type, so we store a reference to the prototype
	let properties = componentUrlProperties.get(prototype);
	if (!properties) {
		componentUrlProperties.set(prototype, properties = [] as string[]);
	}
	properties.push(propertyName);
}

// Property decorator indicating a property to get or set when reading or generating URLs, appended as query strings
export function UrlQuery(prototype: Object, propertyName: string) {
	// Property decorators don't access the class type, so we store a reference to the prototype
	componentUrlQueries.set(prototype, propertyName);
}

// Property decorator indicating a property deciding the page's title (translate: true will pass the value through the translation pipe, so the source component doesn't need to inject it)
export function PageTitle(translate: boolean = false) {
	return function (prototype: Object, propertyName: string) {
		componentTitleProperties.set(prototype, { propertyName, translate });
	}
}
